import React, { FC, ReactNode, useCallback, useState } from 'react'
import styled, { css } from 'styled-components'

interface PanelCreationButtonsZoneProps {
  /**
   * Renders the panel-insert controls (e.g. CreatePanelButtons) and exposes
   * a callback for tracking dropdown open state so the zone stays revealed
   * while a portaled menu (which is outside the hover target) is open.
   */
  renderControls: (props: {
    onDropdownOpenChange: (open: boolean) => void
  }) => ReactNode
  /**
   * When true, the controls are always shown in normal layout flow with no
   * hover requirement (e.g. as a persistent "add panel" footer beneath the
   * last panel in a column). Defaults to false (hover-to-reveal overlay).
   */
  alwaysVisible?: boolean
}

const HoverWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 0.625rem;
  display: flex;
  align-items: center;
  justify-content: center;
`

const ControlsOverlay = styled.div<{ $forceVisible: boolean }>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, calc(-50% + 0.25rem));
  z-index: 20;
  opacity: 0;
  pointer-events: none;
  transition:
    opacity 150ms ease-out 80ms,
    transform 150ms ease-out 80ms;

  ${HoverWrapper}:hover &,
  ${HoverWrapper}:focus-within & {
    opacity: 1;
    pointer-events: auto;
    transform: translate(-50%, -50%);
    transition-delay: 0ms;
  }

  ${({ $forceVisible }) =>
    $forceVisible &&
    css`
      opacity: 1;
      pointer-events: auto;
      transform: translate(-50%, -50%);
      transition-delay: 0ms;
    `}
`

const PersistentWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 0.75rem 0 0.5rem;
`

const RawPanelCreationButtonsZone: FC<PanelCreationButtonsZoneProps> = ({
  renderControls,
  alwaysVisible = false,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)

  const handleDropdownOpenChange = useCallback((open: boolean) => {
    setIsDropdownOpen(open)
  }, [])

  const controls = renderControls({
    onDropdownOpenChange: handleDropdownOpenChange,
  })

  if (alwaysVisible) {
    return <PersistentWrapper>{controls}</PersistentWrapper>
  }

  return (
    <HoverWrapper>
      <ControlsOverlay $forceVisible={isDropdownOpen}>
        {controls}
      </ControlsOverlay>
    </HoverWrapper>
  )
}

export const PanelCreationButtonsZone = React.memo(RawPanelCreationButtonsZone)
